import { useQuery } from '@tanstack/react-query';
import { supabase } from "@/lib/supabase";
import { BaseGame } from './types';
import useUser from '@/hooks/useUser';

const DEFAULT_GAMES: Record<string, BaseGame[]> = {
  basic: [
    {
      id: '6e80c558-2caf-4916-9b56-b51b841c7f53',
      title: 'Calculadora Financiera',
      description: 'Resuelve problemas matemáticos y gana Cerebrix mientras mejoras tus habilidades de cálculo',
      type: 'calculator',
      difficulty: 'basic',
      subscription_type: 'free'
    },
    {
      id: '83f64afa-3bdd-4b8f-a9be-57e1faeb0e22',
      title: 'Cazador de Tesoros',
      description: 'Recolecta premios y tesoros mientras aprendes sobre el valor del dinero y la importancia del ahorro',
      type: 'coins',
      difficulty: 'basic',
      subscription_type: 'free'
    },
    {
      id: '5374d1e0-4784-4a68-9985-006f50fdddae',
      title: 'Memoria Financiera',
      description: 'Encuentra las parejas de cartas mientras aprendes conceptos financieros importantes',
      type: 'memory',
      difficulty: 'basic',
      subscription_type: 'free'
    },
    {
      id: '27f23b41-54a2-46b4-975d-61e50e2e9ef6',
      title: 'Presupuesto Familiar',
      description: 'Aprende a crear y manejar un presupuesto familiar básico',
      type: 'budget',
      difficulty: 'basic',
      subscription_type: 'free'
    },
    {
      id: '9eff9fd7-e942-40b3-825d-9cf9e0415258',
      title: 'Ahorro Inteligente',
      description: 'Aprende estrategias básicas de ahorro y toma decisiones financieras',
      type: 'savings',
      difficulty: 'basic',
      subscription_type: 'free'
    },
    {
      id: 'e86bc100-fd24-475f-b5d1-d9358a3b3278',
      title: 'Mi Primer Negocio',
      description: 'Aprende conceptos básicos de emprendimiento y liderazgo',
      type: 'business',
      difficulty: 'basic',
      subscription_type: 'premium'
    }
  ],
  intermediate: [
    // Juegos intermedios
  ],
  advanced: [
    // Juegos avanzados
  ]
};

export const useGamesData = (difficulty: 'basic' | 'intermediate' | 'advanced') => {
  const { user } = useUser();
  
  // Obtener los juegos disponibles
  const { data: games, isLoading: isGamesLoading } = useQuery({
    queryKey: ['games', difficulty],
    queryFn: async () => {
      try {
        const { data, error } = await supabase
          .from('games')
          .select('*')
          .eq('difficulty', difficulty)
          .order('title');
          
        if (error) {
          console.error('Error fetching games:', error);
          return DEFAULT_GAMES[difficulty] || [];
        }
        
        return data as BaseGame[];
      } catch (error) {
        console.error('Error in games query:', error);
        return DEFAULT_GAMES[difficulty] || [];
      }
    },
    staleTime: 1000 * 60 * 5, // 5 minutos
  });
  
  // Obtener el perfil del usuario
  const { data: userProfile, isLoading: isProfileLoading } = useQuery({
    queryKey: ['user-profile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();
          
        if (error) {
          console.error('Error fetching user profile:', error);
          return null;
        }
        
        return data;
      } catch (error) {
        console.error('Error in profile query:', error);
        return null;
      }
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 5, // 5 minutos
  });
  
  return {
    games: games || DEFAULT_GAMES[difficulty] || [],
    userProfile,
    isLoading: isGamesLoading || isProfileLoading
  };
};
